/**
 * Wholesale Payments API Client
 * Phase 2: Wholesale Partnership Module
 */

import apiClient from '../client';

const BASE_URL = '/wholesale/payments';
const WAYBILL_URL = '/wholesale/waybills';

// ===== LIST & SEARCH =====

/**
 * Get all payments with filters
 */
export const getPayments = async (params = {}) => {
  try {
    const response = await apiClient.get(BASE_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch payments',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get single payment by ID
 */
export const getPaymentById = async (id) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get pending payments
 */
export const getPendingPayments = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/pending`);
    return response.data;
  } catch (error) {
    console.error('Error fetching pending payments:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch pending payments',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get overdue payments
 */
export const getOverduePayments = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/overdue`);
    return response.data;
  } catch (error) {
    console.error('Error fetching overdue payments:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch overdue payments',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get outstanding balances grouped by distributor
 */
export const getOutstandingBalances = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/outstanding`);
    return response.data;
  } catch (error) {
    console.error('Error fetching outstanding balances:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch outstanding balances',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get refunds
 */
export const getRefunds = async (params = {}) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/refunds`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching refunds:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch refunds',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Get unpaid orders for a specific distributor
 */
export const getDistributorUnpaidOrders = async (distributorId) => {
  try {
    const response = await apiClient.get(`/wholesale/distributors/${distributorId}/unpaid-orders`);
    return response.data;
  } catch (error) {
    console.error('Error fetching unpaid orders:', error);
    throw {
      message: error.response?.data?.error || 'Failed to fetch unpaid orders',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== CREATE & VERIFY =====

/**
 * Create/record a new payment
 */
export const createPayment = async (data) => {
  try {
    const response = await apiClient.post(BASE_URL, data);
    return response.data;
  } catch (error) {
    console.error('Error creating payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Verify a payment
 */
export const verifyPayment = async (id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/verify`);
    return response.data;
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to verify payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Reject a payment
 */
export const rejectPayment = async (id, reason) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/reject`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to reject payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Allocate an unallocated payment to an order or account balance
 */
export const allocatePayment = async (id, allocationData) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/allocate`, allocationData);
    return response.data;
  } catch (error) {
    console.error('Error allocating payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to allocate payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Validate if an order can accept a payment
 */
export const validateOrderForPayment = async (orderId) => {
  try {
    const response = await apiClient.get(`${BASE_URL}/validate-order/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error validating order:', error);
    throw {
      message: error.response?.data?.error || 'Failed to validate order',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

// ===== WAYBILLS =====

/**
 * Create a waybill
 */
export const createWaybill = async (data) => {
  try {
    const response = await apiClient.post(WAYBILL_URL, data);
    return response.data;
  } catch (error) {
    console.error('Error creating waybill:', error);
    throw {
      message: error.response?.data?.error || 'Failed to create waybill',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Update waybill status
 */
export const updateWaybillStatus = async (id, status) => {
  try {
    const response = await apiClient.patch(`${WAYBILL_URL}/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating waybill status:', error);
    throw {
      message: error.response?.data?.error || 'Failed to update waybill status',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Cancel a payment
 */
export const cancelPayment = async (id, reason) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/cancel`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error cancelling payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to cancel payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Reactivate a cancelled payment
 */
export const reactivatePayment = async (id) => {
  try {
    const response = await apiClient.post(`${BASE_URL}/${id}/reactivate`);
    return response.data;
  } catch (error) {
    console.error('Error reactivating payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to reactivate payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Delete a payment
 */
export const deletePayment = async (id) => {
  try {
    const response = await apiClient.delete(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting payment:', error);
    throw {
      message: error.response?.data?.error || 'Failed to delete payment',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

/**
 * Download payment notification receipt (simplified receipt without product listing)
 * For standalone payments or additional payments outside of order processing
 */
export const downloadPaymentNotificationReceipt = async (paymentId) => {
  try {
    const response = await apiClient.get(`/wholesale/payments/${paymentId}/notification-receipt`, {
      responseType: 'blob'
    });

    // Create blob and download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `payment-notification-${paymentId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error downloading payment notification receipt:', error);
    throw {
      message: error.response?.data?.error || 'Failed to download payment notification receipt',
      status: error.response?.status,
      details: error.response?.data
    };
  }
};

export default {
  getPayments,
  getPaymentById,
  getPendingPayments,
  getOverduePayments,
  getOutstandingBalances,
  getDistributorUnpaidOrders,
  createPayment,
  verifyPayment,
  rejectPayment,
  allocatePayment,
  validateOrderForPayment,
  cancelPayment,
  reactivatePayment,
  deletePayment,
  downloadPaymentNotificationReceipt,
  createWaybill,
  updateWaybillStatus
};
